/**
 * QR + flyer generator modal (canvas) for sharing the product page.
 */
import { useEffect, useRef, useState } from "react";
import { devError } from "@/lib/log";
import QRCode from "qrcode";
import { WhatsappLogo, X, QrCode, DownloadSimple, Copy } from "@phosphor-icons/react";
import { fmt } from "@/lib/api";
import { toast } from "sonner";

export function QRShareModal({
  publicUrl,
  referral,
  productId,
  nombre,
  precio,
  moneda,
  hero,
  waNumber,
  negocio,
  onClose,
}) {
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [building, setBuilding] = useState(false);
  const flyerRef = useRef(null);

  const shareUrl = referral?.code
    ? `${publicUrl}?ref=${referral.code}`
    : publicUrl;

  const waMsg = encodeURIComponent(
    `Hola, vi este artículo en ${negocio}: ${nombre} — ${fmt(precio, moneda)}.\n¿Sigue disponible?\n${shareUrl}`,
  );
  const waLink = waNumber ? `https://wa.me/${waNumber}?text=${waMsg}` : null;

  // Generate QR
  useEffect(() => {
    QRCode.toDataURL(shareUrl, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 512,
      color: { dark: "#002FA7", light: "#FFFFFF" },
    })
      .then(setQrDataUrl)
      .catch((err) => {
        devError("qr error", err);
        toast.error("No se pudo generar el QR");
      });
  }, [shareUrl]);

  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Enlace copiado");
        return;
      }
      throw new Error("clipboard api unavailable");
    } catch {
      // Fallback for restricted contexts (older browsers, iframes, etc.)
      try {
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) toast.success("Enlace copiado");
        else toast.error("Copia el enlace manualmente");
      } catch {
        toast.error("Copia el enlace manualmente");
      }
    }
  };

  const downloadFlyer = async () => {
    setBuilding(true);
    try {
      // Build flyer on a hidden canvas (1080×1620, 2:3 print aspect)
      const W = 1080;
      const H = 1620;
      const canvas = document.createElement("canvas");
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext("2d");

      // Background
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, W, H);

      // Top brand bar
      ctx.fillStyle = "#002FA7";
      ctx.fillRect(0, 0, W, 90);
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "bold 36px 'Space Grotesk', system-ui, sans-serif";
      ctx.textBaseline = "middle";
      ctx.fillText(negocio, 56, 45);
      ctx.font = "500 22px 'IBM Plex Mono', monospace";
      ctx.textAlign = "right";
      ctx.fillText("VendeIA", W - 56, 45);
      ctx.textAlign = "left";

      // Product image (square, full width with margin)
      if (hero) {
        const img = await loadImage(`data:image/jpeg;base64,${hero}`);
        const padX = 56;
        const imgW = W - padX * 2;
        const imgH = imgW; // square
        // border
        ctx.fillStyle = "#E4E4E7";
        ctx.fillRect(padX - 3, 130 - 3, imgW + 6, imgH + 6);
        ctx.drawImage(img, padX, 130, imgW, imgH);
      } else {
        ctx.fillStyle = "#F4F4F5";
        ctx.fillRect(56, 130, W - 112, W - 112);
      }

      // Product name
      let y = 130 + (W - 112) + 60;
      ctx.fillStyle = "#09090B";
      ctx.font = "bold 56px 'Space Grotesk', system-ui, sans-serif";
      const nameLines = wrapText(ctx, nombre, W - 112);
      nameLines.slice(0, 2).forEach((ln, i) => {
        ctx.fillText(ln, 56, y + i * 64);
      });
      y += nameLines.slice(0, 2).length * 64 + 20;

      // Price
      ctx.fillStyle = "#002FA7";
      ctx.font = "900 84px 'Space Grotesk', system-ui, sans-serif";
      ctx.fillText(fmt(precio, moneda), 56, y + 50);
      y += 110;

      // Divider
      ctx.fillStyle = "#E4E4E7";
      ctx.fillRect(56, y, W - 112, 2);
      y += 30;

      // Bottom block: QR + CTA on right
      const qrSize = 320;
      const qrX = 56;
      const qrY = y;
      if (qrDataUrl) {
        const qrImg = await loadImage(qrDataUrl);
        ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
        // QR caption
        ctx.fillStyle = "#52525B";
        ctx.font = "500 22px 'IBM Plex Mono', monospace";
        ctx.fillText("Escanea para ver el producto", qrX, qrY + qrSize + 36);
      }

      // CTA right side
      const ctaX = qrX + qrSize + 40;
      ctx.fillStyle = "#09090B";
      ctx.font = "bold 36px 'Space Grotesk', system-ui, sans-serif";
      ctx.fillText("Compra ahora", ctaX, qrY + 30);
      ctx.fillStyle = "#52525B";
      ctx.font = "400 24px 'IBM Plex Sans', system-ui, sans-serif";
      const ctaLines = wrapText(
        ctx,
        "Apunta tu cámara al QR o pide informes por WhatsApp.",
        W - ctaX - 56,
      );
      ctaLines.forEach((ln, i) => {
        ctx.fillText(ln, ctaX, qrY + 80 + i * 32);
      });

      // WhatsApp pill
      if (waNumber) {
        const pillY = qrY + 200;
        ctx.fillStyle = "#25D366";
        ctx.fillRect(ctaX, pillY, W - ctaX - 56, 70);
        ctx.fillStyle = "#FFFFFF";
        ctx.font = "bold 28px 'IBM Plex Mono', monospace";
        ctx.textBaseline = "middle";
        ctx.fillText(`WhatsApp: +${waNumber}`, ctaX + 24, pillY + 35);
        ctx.textBaseline = "alphabetic";
      }

      // Footer URL
      ctx.fillStyle = "#52525B";
      ctx.font = "400 18px 'IBM Plex Mono', monospace";
      const footerY = H - 40;
      const footerText = shareUrl.length > 70 ? shareUrl.slice(0, 67) + "..." : shareUrl;
      ctx.fillText(footerText, 56, footerY);

      // Trigger download
      const link = document.createElement("a");
      link.download = `volante-${slugify(nombre)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Volante descargado");
    } catch (e) {
      devError("flyer error", e);
      toast.error("No se pudo generar el volante");
    } finally {
      setBuilding(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="qr-share-modal"
    >
      <div
        className="bg-white border border-zinc-200 max-w-md w-full p-6 animate-fade-in max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="overline text-klein flex items-center gap-1.5">
              <QrCode size={12} weight="fill" /> Compartir producto
            </div>
            <h2 className="h-display text-2xl font-bold tracking-tight mt-1">
              QR + WhatsApp
            </h2>
            <p className="text-xs text-muted2 mt-1">
              Imprime un volante o comparte el enlace directo.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100" data-testid="qr-close">
            <X size={18} />
          </button>
        </div>

        <div ref={flyerRef} className="border border-zinc-200 p-4 flex flex-col items-center gap-3 bg-zinc-50">
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt="QR del producto"
              className="w-48 h-48"
              data-testid="qr-image"
            />
          ) : (
            <div className="w-48 h-48 bg-zinc-200 animate-pulse" />
          )}
          <div className="text-center">
            <div className="font-medium text-sm truncate max-w-[260px]">{nombre}</div>
            <div className="h-display text-2xl font-black text-klein tracking-tight mt-1">
              {fmt(precio, moneda)}
            </div>
          </div>
          {referral?.code && (
            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 bg-[#E8F5E9] text-[#1B5E20] border border-[#C8E6C9]">
              Con código {referral.code} (-{referral.discount_pct}%)
            </span>
          )}
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <button
            onClick={downloadFlyer}
            disabled={building || !qrDataUrl}
            className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-klein text-white text-sm font-semibold hover:bg-klein-dark disabled:opacity-50 w-full"
            data-testid="btn-download-flyer"
          >
            <DownloadSimple size={16} weight="bold" />
            {building ? "Generando…" : "Descargar volante (PNG)"}
          </button>
          {waLink && (
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-white text-sm font-semibold hover:bg-[#1eb858] w-full"
              data-testid="btn-open-whatsapp"
            >
              <WhatsappLogo size={16} weight="fill" /> Abrir WhatsApp con mensaje listo
            </a>
          )}
          <button
            onClick={copyLink}
            className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-zinc-300 text-sm font-medium hover:bg-zinc-50 w-full"
            data-testid="btn-copy-link"
          >
            <Copy size={16} /> Copiar enlace
          </button>
        </div>

        <div className="mt-4 p-3 bg-zinc-50 border-l-2 border-klein text-[11px] text-muted2 break-all font-mono leading-relaxed">
          {shareUrl}
        </div>
        <p className="mt-2 text-[10px] text-muted2 leading-relaxed">
          Tip: imprime el volante en tamaño media carta o pégalo en mostrador. El cliente escanea con su cámara y entra directo a esta página.
        </p>
        {productId && <input type="hidden" value={productId} readOnly />}
      </div>
    </div>
  );
}


function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}


function wrapText(ctx, text, maxWidth) {
  const words = String(text || "").split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}


function slugify(s) {
  return String(s || "producto")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 40);
}
